import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTypedSelector } from '../../../store/store';
import { useTrackEvent } from '../../../common/hooks/useTrackEvent';
import { EventNames, maxSizeUploadedUserFile } from '../../../common/constants/constants';
import { ErrorsStrings } from '../../../common/localization/en';
import { ErrorDialog } from '../../../common/components/ErrorDialog/ErrorDialog';
import { useClientType } from '../../../services/hooks/useClientType';
import { SV_USER_TERMS } from '../../../common/components/AuthForm/AuthForm.helper';
import { getCachedUploadSpeed } from '../../VideoToolPage/utils';
import { oncePerPageLoad, startAttempt } from '../../../services/analytics/telemetry';
import { primeScreenWakeLock } from '../../../services/upload/useUploadWakeLock';
import { getHeroArtwork } from './heroArtwork';
import { WelcomeVideoModal } from '../WelcomeVideoModal/WelcomeVideoModal';
import { TermsOfUse } from '../../SignUpFlowPage/SignUpScreen/TermsOfUse/TermsOfUse';
import { TicketPage, TicketPaper, TicketPerforation } from '../Ticket/Ticket';
import {
  HeroSection,
  HeroFrame,
  HeroImage,
  HeroSkeleton,
  HeroBadge,
  ActionsSection,
  ChooseButton,
  RecordButton,
  ExampleLink,
  FooterSection,
  StatsRow,
  Stat,
  StatDot,
  SpeedSkeleton,
  TermsLine,
  Squiggle,
} from './HomeScreen.styles';

/* ─── Icons (geometry traced from the mockup) ─── */

// Filled picture icon on the Choose button: a plate in the button's text
// color with the mountains + sun knocked out in the brand color (see
// ChooseButton).
const ChoosePictureIcon = (
  <svg viewBox="0 0 36 32" aria-hidden>
    <rect x="0.5" y="0.5" width="35" height="31" rx="5" fill="currentColor" />
    <circle className="knockout" cx="25" cy="10" r="3.2" />
    <path className="knockout" d="M5 26l8.5-11 6.2 7.6 3.6-4.3L31 26z" />
  </svg>
);

// Placeholder badge glyph: white mountains + sun, no frame.
const BadgePictureIcon = (
  <svg viewBox="0 0 42 42" fill="#fff" aria-hidden>
    <circle cx="29.5" cy="12" r="4" />
    <path d="M4 33l12.5-15.5 8 9.8 5.5-6.3L38 33z" />
  </svg>
);

const CameraIcon = (
  <svg
    viewBox="0 0 27 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="1" y="1.5" width="16.5" height="17" rx="3" />
    <path d="M17.5 8l8-4.5v13l-8-4.5z" />
  </svg>
);

const DatabaseIcon = (
  <svg
    viewBox="0 0 29 31"
    fill="none"
    stroke="#919397"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <ellipse cx="14.5" cy="6.5" rx="12.5" ry="5" />
    <path d="M2 6.5v9c0 2.8 5.6 5 12.5 5S27 18.3 27 15.5v-9" />
    <path d="M2 15.5v9c0 2.8 5.6 5 12.5 5S27 27.3 27 24.5v-9" />
  </svg>
);

const UploadArrowIcon = (
  <svg viewBox="0 0 34 34" fill="none" stroke="#7b7b7b" strokeWidth={1.7} strokeLinejoin="round" aria-hidden>
    <path d="M17 2.5L31.5 17.5H23V31.5H11V17.5H2.5z" />
  </svg>
);

const PlayIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9.2" />
    <path d="M10.2 8.3l5.2 3.7-5.2 3.7z" fill="currentColor" stroke="none" />
  </svg>
);

const MAX_FILE_MB = Math.round(maxSizeUploadedUserFile / 1_000_000);

// On phones and tablets, Record opens the device's own camera app instead of
// the in-page recorder. The clip comes back as a finished file and takes the
// same path as a library pick. Desktop keeps the in-page recorder.
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS reports a desktop Mac UA; touch points give it away.
  const isMobileUa =
    /Android|iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return isMobileUa && !!window.matchMedia?.('(pointer: coarse)').matches;
};

type PickerSource = 'library' | 'camera';

interface Props {
  onFileSelect: (file: File) => void;
  onRecordClick: () => void;
  isButtonsDisabled: boolean;
  /** Why the buttons are disabled right now (for ticket_ready telemetry). */
  disabledReason: 'terms' | 'captcha' | null;
  isTermsAccepted: boolean | null;
  onTermsChange: (isChecked: boolean | null) => void;
}

export const HomeScreen: FC<Props> = ({
  onFileSelect,
  onRecordClick,
  isButtonsDisabled,
  disabledReason,
  isTermsAccepted,
  onTermsChange,
}) => {
  const { trackEvent } = useTrackEvent();
  const { isTrubluClient } = useClientType();

  const { id: userId } = useTypedSelector((state) => state.me);
  const {
    account: { id: accountId },
    accountAttributes: { properties },
  } = useTypedSelector((state) => state.account);
  const { currentCampaign } = useTypedSelector((state) => state.rewards);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const [isErrorAlert, setIsErrorAlert] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [heroError, setHeroError] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  // ── Telemetry (spec v2) ──
  // Each asset is reported once per page load; coming back from the
  // recorder remounts the ticket but loads nothing new.
  const reportAsset = (props: { asset: string; outcome: string; [k: string]: string | number | null }) => {
    if (oncePerPageLoad(`ticket_asset_${props.asset}`)) {
      trackEvent({ action: EventNames.ticket_asset_load, ...props });
    }
  };
  const mountedAtRef = useRef(performance.now());
  const readyAtRef = useRef<number | null>(null);
  const tapCountRef = useRef(0);
  const pickerOpenedAtRef = useRef<number | null>(null);
  const pickerResolvedRef = useRef(true);
  const pickerSourceRef = useRef<PickerSource>('library');
  const disabledReasonRef = useRef(disabledReason);
  disabledReasonRef.current = disabledReason;
  const speedRef = useRef<number | null>(null);

  // ticket_ready: first time the buttons become usable.
  useEffect(() => {
    if (isButtonsDisabled || readyAtRef.current !== null) return;
    readyAtRef.current = performance.now();
    if (!oncePerPageLoad('ticket_ready')) return;
    trackEvent({
      action: EventNames.ticket_ready,
      gated_by: disabledReasonRef.current ?? 'none',
      wait_ms: Math.round(readyAtRef.current - mountedAtRef.current),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isButtonsDisabled]);

  // Safari only grants a screen wake lock inside a tap, and the upload starts
  // long after one. The first tap on the ticket (capture phase, so before
  // the picker opens) earns the grant the upload screen relies on later.
  useEffect(() => {
    const onTap = () => primeScreenWakeLock(trackEvent);
    document.addEventListener('click', onTap, true);
    return () => document.removeEventListener('click', onTap, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // file_picker_result: the picker closing without a change = cancelled.
  // Detected by window focus returning with no `change` within a beat.
  useEffect(() => {
    const onFocus = () => {
      if (pickerOpenedAtRef.current === null || pickerResolvedRef.current) return;
      const openedAt = pickerOpenedAtRef.current;
      setTimeout(() => {
        if (pickerResolvedRef.current) return;
        pickerResolvedRef.current = true;
        trackEvent({
          action: EventNames.file_picker_result,
          outcome: 'cancelled',
          picker: pickerSourceRef.current,
          ms_in_picker: Math.round(performance.now() - openedAt),
          tap_index: tapCountRef.current,
        });
      }, 800);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasWelcomeVideo = !!currentCampaign?.welcomeVideoUrl && currentCampaign.showWelcomeVideo;

  // Ticket artwork: an explicit per-account image when configured, else the
  // industry image for the client type (UWM → brokers, SV → travel). Always
  // resolvable, so the hero never falls back to the welcome-video poster or
  // the grey placeholder.
  const {
    account: { clientType },
  } = useTypedSelector((state) => state.account);
  const webappConfig = properties?.['webapp.config'] as
    | { 'hero-image-url'?: string; 'hero-industry'?: string }
    | undefined;
  const hero = getHeroArtwork({
    clientType,
    heroImageUrl: webappConfig?.['hero-image-url'] || null,
    heroIndustry: webappConfig?.['hero-industry'] || null,
  });
  const heroSrc = hero.src;
  const heroSource: 'account' | 'industry' | 'placeholder' = heroError ? 'placeholder' : hero.source;
  const showHeroImage = !!heroSrc && !heroError;
  // Skeleton only while the artwork is downloading.
  const heroPending = showHeroImage && !heroLoaded;

  // A new source means a new download — unless the browser already has it
  // (cache, or the element completed before React attached onLoad), in which
  // case `load` never fires again and we'd be stuck on the skeleton.
  const heroImgRef = useRef<HTMLImageElement | null>(null);
  const isComplete = (img: HTMLImageElement | null) => !!img && img.complete && img.naturalWidth > 0;
  useEffect(() => {
    setHeroError(false);
    setHeroLoaded(isComplete(heroImgRef.current));
  }, [heroSrc]);

  // Upload-speed badge. Measurement runs upstream in HomePage
  // (useBackgroundUploadSpeedTest) and is cached in sessionStorage; poll the
  // cache until a value lands.
  const [speedMbps, setSpeedMbps] = useState<number | null>(() =>
    typeof window !== 'undefined' ? getCachedUploadSpeed()?.speedMbps ?? null : null,
  );
  speedRef.current = speedMbps;
  // The landing test writes a partial average after every round, so keep
  // reading the cache until the final round (or a 60 s cap) instead of
  // freezing on the first 256 KB reading, which under-reports fast lines.
  const speedReportedRef = useRef(false);
  useEffect(() => {
    const startedAt = performance.now();
    const read = () => {
      const cached = getCachedUploadSpeed();
      if (!cached) return false;
      setSpeedMbps((prev) => (prev === cached.speedMbps ? prev : cached.speedMbps));
      if (!speedReportedRef.current) {
        speedReportedRef.current = true;
        reportAsset({
          asset: 'speed',
          outcome: 'loaded',
          ms: Math.round(performance.now() - mountedAtRef.current),
        });
      }
      return cached.rounds >= 3;
    };
    if (read()) return undefined;
    const id = setInterval(() => {
      if (read() || performance.now() - startedAt > 60_000) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadCardClick = (source: 'button' | 'hero' = 'button') => {
    if (isButtonsDisabled) return;
    tapCountRef.current += 1;
    pickerOpenedAtRef.current = performance.now();
    pickerResolvedRef.current = false;
    pickerSourceRef.current = 'library';
    inputRef.current?.click();
    trackEvent({
      action: EventNames.Upload_Button_Click,
      location: window.location.href,
      accountId,
      userId,
    });
    trackEvent({
      action: EventNames.choose_video_click,
      source,
      tap_index: tapCountRef.current,
      speed_shown_mbps: speedRef.current != null ? Math.round(speedRef.current) : null,
      ticket_ready_ms_ago: readyAtRef.current !== null ? Math.round(performance.now() - readyAtRef.current) : null,
    });
  };

  const handleFileChange = (picker: PickerSource) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    // Clear input so same file can be re-selected
    event.target.value = '';

    const msInPicker =
      pickerOpenedAtRef.current !== null ? Math.round(performance.now() - pickerOpenedAtRef.current) : null;
    pickerResolvedRef.current = true;
    trackEvent({
      action: EventNames.file_picker_result,
      outcome: 'selected',
      picker,
      ms_in_picker: msInPicker,
      tap_index: tapCountRef.current,
    });

    trackEvent({
      action: EventNames.Create_Video_Page_file_selected,
      location: window.location.href,
      accountId,
      userId,
      fileSize: file.size,
      page: EventNames.Create_Video_Page,
    });

    if (file.size >= maxSizeUploadedUserFile) {
      setIsErrorAlert(true);
      trackEvent({ action: EventNames.FileSizeLimitError, size: file.size });
      trackEvent({
        action: EventNames.file_rejected,
        reason: 'size_limit',
        file_size: file.size,
        mime: file.type || null,
        limit_value: maxSizeUploadedUserFile,
      });
      return;
    }

    // The upload attempt starts here: every later event joins on attempt_id.
    startAttempt(picker === 'camera' ? 'camera' : 'picker', file.size);
    trackEvent({
      action: EventNames.file_selected,
      picker,
      file_size: file.size,
      mime: file.type || null,
      ext: (file.name.split('.').pop() || '').toLowerCase().slice(0, 8) || null,
      ms_in_picker: msInPicker,
      tap_index: tapCountRef.current,
    });

    onFileSelect(file);
  };

  const handleRecordClick = () => {
    if (isButtonsDisabled) return;
    trackEvent({
      action: EventNames.Capture_Button_Click,
      location: window.location.href,
      accountId,
      userId,
    });
    tapCountRef.current += 1;
    trackEvent({ action: EventNames.record_video_click, tap_index: tapCountRef.current });
    if (isMobileDevice()) {
      // Must stay inside the tap: browsers only open the camera from a user
      // gesture.
      pickerOpenedAtRef.current = performance.now();
      pickerResolvedRef.current = false;
      pickerSourceRef.current = 'camera';
      cameraInputRef.current?.click();
      return;
    }
    onRecordClick();
  };

  const disabledSx = {
    opacity: isButtonsDisabled ? 0.5 : 1,
    pointerEvents: isButtonsDisabled ? ('none' as const) : ('auto' as const),
  };

  return (
    <>
      <ErrorDialog
        errorMessage={ErrorsStrings.FileSizeExceeded}
        isOpen={isErrorAlert}
        onClose={() => setIsErrorAlert(false)}
      />

      {hasWelcomeVideo && (
        <WelcomeVideoModal
          open={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          videoUrl={currentCampaign!.welcomeVideoUrl!}
        />
      )}

      <TicketPage
        onLogoLoad={() =>
          reportAsset({ asset: 'logo', outcome: 'loaded', ms: Math.round(performance.now() - mountedAtRef.current) })
        }
        onLogoError={() =>
          reportAsset({ asset: 'logo', outcome: 'error', ms: Math.round(performance.now() - mountedAtRef.current) })
        }
      >
        <TicketPaper>
          <HeroSection>
            <HeroFrame
              type="button"
              onClick={() => handleUploadCardClick('hero')}
              disabled={isButtonsDisabled}
              aria-label="Choose a video from your library"
              aria-busy={heroPending}
            >
              {showHeroImage && (
                <HeroImage
                  ref={heroImgRef}
                  src={heroSrc}
                  srcSet={hero.srcSet}
                  sizes={hero.sizes}
                  alt=""
                  $ready={heroLoaded}
                  onLoad={() => {
                    setHeroLoaded(true);
                    reportAsset({
                      asset: 'hero',
                      outcome: 'loaded',
                      hero_source: heroSource,
                      hero_industry: hero.industry,
                      ms: Math.round(performance.now() - mountedAtRef.current),
                    });
                  }}
                  onError={() => {
                    setHeroError(true);
                    reportAsset({
                      asset: 'hero',
                      outcome: 'error',
                      hero_source: heroSource,
                      ms: Math.round(performance.now() - mountedAtRef.current),
                    });
                  }}
                />
              )}
              {heroPending ? <HeroSkeleton /> : <HeroBadge>{BadgePictureIcon}</HeroBadge>}
            </HeroFrame>
          </HeroSection>

          <TicketPerforation />

          <ActionsSection>
            <ChooseButton
              type="button"
              onClick={() => handleUploadCardClick('button')}
              aria-label="Choose a video from your library"
              style={disabledSx}
            >
              {ChoosePictureIcon}
              <span>Choose Video</span>
            </ChooseButton>

            <RecordButton type="button" onClick={handleRecordClick} aria-label="Record a video" style={disabledSx}>
              {CameraIcon}
              <span>Record Video</span>
            </RecordButton>

            {hasWelcomeVideo && (
              <ExampleLink
                type="button"
                onClick={() => setIsVideoModalOpen(true)}
                aria-label="Watch a short example video"
              >
                {PlayIcon}
                <span>Not sure what to share? Watch an example</span>
              </ExampleLink>
            )}
          </ActionsSection>

          <TicketPerforation />

          <FooterSection>
            <StatsRow>
              <Stat>
                {DatabaseIcon}
                <span>
                  Up to 60 sec
                  <StatDot>•</StatDot>
                  {MAX_FILE_MB} MB max
                </span>
              </Stat>
              <Stat role="status" aria-live="polite">
                {UploadArrowIcon}
                <span>
                  {speedMbps != null ? (
                    `${speedMbps > 0 && speedMbps < 1 ? '<1' : Math.round(speedMbps)} Mbps`
                  ) : (
                    <SpeedSkeleton aria-label="Measuring upload speed" />
                  )}
                  <StatDot>•</StatDot>
                  Upload Speed
                </span>
              </Stat>
            </StatsRow>

            {isTrubluClient ? (
              <TermsOfUse isTermsAccepted={isTermsAccepted} onChange={onTermsChange} />
            ) : (
              <TermsLine>
                By uploading, you agree to our{' '}
                <a
                  href={SV_USER_TERMS}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent({ action: EventNames.terms_link_click, variant: 'implicit', link: 'terms' })}
                >
                  Terms of Use
                </a>
              </TermsLine>
            )}

            <Squiggle
              viewBox="0 0 44 70"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.7}
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M1 9c6-5 12-5 18 0s12 5 18 0 5-3 6-3" />
              <path d="M1 27c6-5 12-5 18 0s12 5 18 0 5-3 6-3" />
              <path d="M1 45c6-5 12-5 18 0s12 5 18 0 5-3 6-3" />
              <path d="M1 63c6-5 12-5 18 0s12 5 18 0 5-3 6-3" />
            </Squiggle>
          </FooterSection>
        </TicketPaper>
      </TicketPage>

      {/* Hidden file input — same accept types as CreateVideo.
          iOS Safari silently ignores programmatic .click() on display:none
          inputs, so we use the visually-hidden pattern (in layout, clipped). */}
      <input
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        ref={inputRef}
        className="visually-hidden"
        onChange={handleFileChange('library')}
      />
      <input
        type="file"
        accept="video/*"
        capture="user"
        ref={cameraInputRef}
        className="visually-hidden"
        onChange={handleFileChange('camera')}
      />
    </>
  );
};
